import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import {
    InventoryReportingExportFormat,
    InventoryReportingExportRequest,
    InventoryReportingExportResponse,
} from '../../types/reporting';

const XLSX_MIME =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

function triggerDownload(blob: Blob, filename: string) {
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

function decodeBase64ToBytes(base64: string): Uint8Array {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

function parseExportPayload(data: unknown): InventoryReportingExportResponse {
    if (typeof data === 'string') {
        const trimmed = data.trim();
        if (trimmed.startsWith('{')) {
            return JSON.parse(trimmed) as InventoryReportingExportResponse;
        }
        throw new Error('Unexpected export response');
    }
    if (data && typeof data === 'object') {
        return data as InventoryReportingExportResponse;
    }
    throw new Error('Unexpected export response');
}

export function getInventoryReportingExportErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError<{ message?: string }>;
        const message = axiosError.response?.data?.message;
        if (message) return message;
    }
    if (error instanceof Error && error.message) return error.message;
    return 'Export failed. Try again.';
}

function exportFilename(
    format: InventoryReportingExportFormat,
    startDate: string,
    endDate: string,
): string {
    return `inventory_changes_by_adjustment_${startDate}_${endDate}.${format}`;
}

async function exportInventoryReporting(
    sessionId: string,
    input: InventoryReportingExportRequest,
): Promise<void> {
    const { data } = await axios.post<InventoryReportingExportResponse>(
        `${API_URL}/reporting/auditing/inventory/export`,
        input,
        { headers: { Authorization: sessionId } },
    );

    const payload = parseExportPayload(data);
    const filename = exportFilename(input.format, input.startDate, input.endDate);

    if (input.format === 'xlsx') {
        if (!payload.xlsx) {
            throw new Error('Export did not include an Excel file.');
        }
        const bytes = decodeBase64ToBytes(payload.xlsx);
        triggerDownload(new Blob([bytes], { type: XLSX_MIME }), filename);
        return;
    }

    if (!payload.csv) {
        throw new Error('Export did not include a CSV file.');
    }
    triggerDownload(new Blob([payload.csv], { type: 'text/csv;charset=utf-8' }), filename);
}

export const useExportInventoryReporting = (
    options?: UseMutationOptions<void, unknown, InventoryReportingExportRequest>,
) => {
    const sessionContext = useContext(SessionContext);

    return useMutation({
        mutationFn: (input: InventoryReportingExportRequest) =>
            exportInventoryReporting(sessionContext?.sessionId ?? '', input),
        ...options,
    });
};
